import { useState, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import Text from 'atoms/Text';
import { DropdownStyles } from 'helper/styles';
import CustomMultiSelect from './CustomMultiSelect';

export const SELECT_ALL_OPTION = { label: 'Select all', value: '*' };

const AsyncReactSelectPaginateSelectAll = ({
  id,
  name,
  placeholder,
  options = [],
  label,
  isRequired,
  isError,
  errorMessage,
  control,
  register = () => {},
  required,
  size,
  loadOptions = () => {},
  isMulti = false,
  isClearable = true,
  menuPlacement = 'auto',
  menuPosition = 'fixed',
  title = '',
  isNotes = false,
  note = '',
  textColor= '',
  readOnly = false,
  color = '#53565A',
  maxHeight = '300px',
  isSimpleSelectAll = false,
  ...rest
}) => {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchedValue, setSearchedValue] = useState('')
  const allOptionsMap = useRef(new Map());

  const getAllOptions = () => Array.from(allOptionsMap.current.values());

  const wrappedLoadOptions = async (inputValue, prevOptions = [], { selectValue } = {}) => {
    const result = await loadOptions(inputValue, prevOptions);
    const newOptions = result?.options || [];

    newOptions.forEach((opt) => {
      if (opt?.value !== SELECT_ALL_OPTION.value && !allOptionsMap.current.has(opt.value)) {
        allOptionsMap.current.set(opt.value, opt);
      }
    });

    if (Array.isArray(selectValue)) {
      selectValue.forEach((opt) => {
        if (opt?.value !== SELECT_ALL_OPTION.value) {
          allOptionsMap.current.set(opt.value, opt);
        }
      });
    }

    return {
      options: prevOptions.length === 0 ? [SELECT_ALL_OPTION, ...newOptions] : [...newOptions],
      hasMore: result.hasMore,
    };
  };

  const components = isMulti
    ? {
      Option: (props) => {
        const selected = props.selectProps.value || [];
        const filteredSelected = selected.filter((opt) => opt.value !== SELECT_ALL_OPTION.value);
        const isAllSelected = filteredSelected.length === getAllOptions().length && getAllOptions().length > 0;
        const isSimpleAllSelected = isSimpleSelectAll && selected.some(opt => opt.value === SELECT_ALL_OPTION.value);
        const isChecked = isSimpleAllSelected || props.isSelected;

        return (
          <CustomMultiSelect
            {...props}
            isSelected={isChecked}
            isAllSelected={props.data.value === SELECT_ALL_OPTION.value ? (isSimpleSelectAll ? isSimpleAllSelected : isAllSelected) : undefined}
          />
        );
      },
    }
    : {};

  const handleChange = (selected, onChange = () => {}) => {
    const allOptions = getAllOptions();
    const hasSelectAll = selected?.some((opt) => opt.value === SELECT_ALL_OPTION.value);
    const filtered = selected?.filter((opt) => opt.value !== SELECT_ALL_OPTION.value) || [];
    const tempArr = [];

    if (isSimpleSelectAll) {
      if (hasSelectAll) {
        onChange?.([SELECT_ALL_OPTION]);
        rest.onChange?.([SELECT_ALL_OPTION]);
        setSearchedValue('');
        return;
      }
      onChange?.(filtered);
      setSearchedValue('')
      rest.onChange?.(filtered);
    } else {
      if (hasSelectAll && filtered.length === allOptions.length) {
      } else if (hasSelectAll) {
        const validIds = [...allOptions.filter((opt) => opt.label.toLowerCase().includes(searchedValue.toLowerCase())), ...filtered].map((a) => a.value)
        tempArr.push(...allOptions.filter((a) => validIds.includes(a.id)))
      }
      else {
        tempArr.push(...filtered);
      }
      onChange?.(tempArr);
      setSearchedValue('')
      rest.onChange?.(tempArr);
    }
  };

  const renderAsyncPaginate = (fieldProps) => (
    <AsyncPaginate
      {...fieldProps}
      {...rest}
      id={id}
      name={name}
      isMulti={isMulti}
      debounceTimeout={300}
      defaultOptions={false}
      loadOptions={wrappedLoadOptions}
      styles={DropdownStyles(isError, size, textColor, readOnly, color, maxHeight)}
      components={components}
      placeholder={placeholder}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      isClearable={isClearable}
      hideSelectedOptions={false}
      menuShouldScrollIntoView={false}
      className={isMulti ? 'multiSelectScroll' : ''}
      onChange={(selected, ...rest) => handleChange(selected, fieldProps?.onChange, rest)}
      onInputChange={(inputValue) => {
        setSearchedValue(inputValue)
        return inputValue;
      }}
    />
  );

  return (
    <div className={rest.className || ''}>
      {title && <Text variant="bodySemibold" className="mb-4 text-base">{title}</Text>}

      {label && (
        <label className="text-[12px] text-gray-1100 mb-1 block leading-4">
          {label}
          {isRequired && <span className="text-red-700 ml-1">*</span>}
        </label>
      )}

      <div className={rest.inputClassName || ''}>
        {control ? (
          <Controller
            control={control}
            name={name}
            rules={{ required }}
            render={({ field }) => renderAsyncPaginate(field)}
          />
        ) : (
          renderAsyncPaginate(register(name))
        )}
      </div>

      {isError && <div className="text-red-900 text-[12px] pt-1 error-in-field">{errorMessage}</div>}
      {isNotes && <div className="text-gray-900 text-xs mt-1">{note}</div>}
    </div>
  );
};

export default AsyncReactSelectPaginateSelectAll;